import { PRIMARY_DATE_SORT } from './cards';
import { REVIEW_FILTERS } from './itemsReview';
import { DEFAULT_GROUP_TOKEN } from './itemGroups';
import { readUrlParams } from './urlParams';
import { sortThenOf } from './sortLevels';
import { columnsFromParam } from './savedFilterView';

/**
 * Grid/table/map mode for the collection items page.
 *
 * The mode is a per-user convenience stored per collection in localStorage,
 * but the ACTIVE mode also rides in the URL (?view=grid|table, written when
 * the user toggles the switch) so other surfaces — the header's "Export
 * view…", which renders outside the page — can see exactly what the user is
 * looking at, the same way the card filter and sort reach them.
 */

// 'worklist' is offered only where a card declares one (utils/cards.js
// cardWorklistFor); it is a MODE like the others so a link to a pass survives
// a refresh and reaches the export menu the same way.
// 'map' (KPR-392) is offered only where the build has map tiles
// (config/mapTiles.js) and a card the list can return has a location
// element; anywhere else a `?view=map` or a remembered map reads as the grid.
// A bundle from before it never sees 'map' as a mode: its own list lacks it,
// so the address falls back to the stored choice and a stored 'map' to the
// grid (readStoredItemsViewMode below took only 'table' then).
// 'board' (KPR-1395) is offered only where every card the list can return
// shares an element a board can be drawn by (utils/board.js); anywhere else
// `?view=board` and a remembered board read as the grid, as the map does.
export const ITEMS_VIEW_MODES = ['grid', 'table', 'worklist', 'map', 'board'];

export const itemsViewModeKey = collectionId => `keepr.itemsView.${collectionId || ''}`;

export const readStoredItemsViewMode = (collectionId) => {
	try {
		const stored = window.localStorage.getItem(itemsViewModeKey(collectionId));
		// 'worklist' is deliberately NOT sticky: it depends on which card is
		// filtered, and a remembered mode that silently does not apply reads
		// as a broken toggle. 'map' IS remembered (KPR-392): whether a list
		// can be mapped is a property of the collection's cards, so a
		// collection someone reads as a map opens as one. 'board' too
		// (KPR-1395), for the same reason.
		return stored === 'table' || stored === 'map' || stored === 'board' ? stored : 'grid';
	} catch {
		return 'grid';
	}
};

/**
 * The collection list's storage for the grid/table choice: the per-collection
 * localStorage key, behind the small store every view-mode owner shares
 * (`useViewMode`). `id` names what the store reads; an account store (a
 * related list's, KPR-722) implements the same three members. Writing never
 * throws: the choice is a per-viewer convenience.
 */
export const localItemsViewStore = collectionId => ({
	id: `local:${collectionId || ''}`,
	read: () => readStoredItemsViewMode(collectionId),
	write: (mode) => {
		try {
			window.localStorage.setItem(itemsViewModeKey(collectionId), mode);
		} catch { /* per-viewer convenience only */ }
	},
});

/**
 * The active items view mode as another surface sees it: the URL param wins
 * (it is what is on screen), the stored per-collection preference fills in
 * when the URL says nothing, and 'grid' is the floor — the page's own default.
 */
export const resolveItemsViewMode = (searchParams, collectionId) => {
	const fromUrl = searchParams?.get('view');
	if (ITEMS_VIEW_MODES.includes(fromUrl)) return fromUrl;
	return readStoredItemsViewMode(collectionId);
};

/**
 * The items page's view in the address: a CONTRACT, not the page's private
 * business (KPR-1579). `hooks/useItemsViewParams` reads and writes it for the
 * page; components/collections/CollectionActions (the header's ⋯: Export
 * view…, Share this view, which feeds components/items/ItemsViewShareButton)
 * reads it with `readItemsView` below, because the header shares no other
 * channel with the page. Renaming a param, or changing what an absent one
 * means, is done here, once, for all three. `?view=`, `?board=`, `?lanes=` and
 * `?attach=` are read by the page itself (hooks/useViewMode, the board,
 * hooks/useItemsBulkState), not here.
 *
 * The spec is `utils/urlParams`': ids and keys are read as the address has
 * them (`trim: false`), the query text is trimmed.
 */
export const ITEMS_VIEW_PARAMS = Object.freeze({
	card: { trim: false },
	// KQL filter text. Absent or blank = no filter (see utils/kql).
	q: { default: '' },
	// The intake quarantine (G9): absent = the collection as its people have
	// accepted it; 'pending' = the review queue; 'accepted' = accepted
	// submissions only; 'any' = both. A plain parameter, not a KQL subject.
	// Anything else reads as no filter, so a mangled link degrades to the
	// ordinary list instead of a 400.
	review: { values: REVIEW_FILTERS, trim: false },
	sort: { default: PRIMARY_DATE_SORT, trim: false },
	// Anything but 'asc' reads as newest first.
	dir: { default: 'desc', values: ['asc', 'desc'], trim: false },
	// "Then by" (KPR-1636): sort levels 2 and 3, each breaking the ties the
	// levels before it leave (utils/sortLevels). Absent = one level, which is
	// every address from before. A direction reads as `dir` does.
	sort2: { trim: false },
	dir2: { default: 'desc', values: ['asc', 'desc'], trim: false },
	sort3: { trim: false },
	dir3: { default: 'desc', values: ['asc', 'desc'], trim: false },
	item: { trim: false },
	// The grid's separators (utils/itemGroups). Raw: the page resolves it
	// against the stored preference (?group= wins once the user has touched the
	// control, exactly like ?view=). The day is the floor: deleted from the URL
	// rather than written.
	group: { default: DEFAULT_GROUP_TOKEN, kind: 'raw' },
	// Raw too: whether linked collections' records join the list is resolved
	// against whether there are any (utils/linkedRecords resolveLinkedParam).
	linked: { kind: 'raw' },
	// The applied saved filter's table columns (KPR-1641, utils/savedFilterView):
	// one token per column (`#<element id>` or a system column, `*` after a
	// frozen one). The filter's own: never written to anyone's table layout.
	// Absent = the person's table layout, as before.
	cols: { trim: false },
	// Which saved filter is applied (KPR-1642): its id, which the saved filter
	// menu names, and compares the screen with, to show a dot when it has
	// changed. Changes nothing the list asks. Absent = none (or the filter
	// whose stored parts are on screen, found by the menu).
	saved: { trim: false },
});

/**
 * The view from `readUrlParams(searchParams, ITEMS_VIEW_PARAMS)` and whether the
 * address names a param (`has`), in the names the page and the header use.
 */
export const itemsViewOf = (params, has) => ({
	cardFilter: params.card,
	query: params.q,
	review: params.review,
	sortField: params.sort,
	sortDirection: params.dir,
	// Levels 2 and 3 as `[{ field, direction }]` (utils/sortLevels): [] for
	// one level. A level 3 with no level 2 is compacted into level 2.
	sortThen: sortThenOf([
		{ field: params.sort2, direction: params.dir2 },
		{ field: params.sort3, direction: params.dir3 },
	]),
	// Whether the reader chose an order, as opposed to inheriting the default.
	// A further level is a choice too: it overrides a table layout's order.
	sortChosen: has('sort') || has('dir') || has('sort2') || has('sort3'),
	selectedItemId: params.item,
	groupParam: params.group,
	linkedParam: params.linked,
	// The applied saved filter's columns (KPR-1641) as saved columns
	// (utils/savedFilterView), [] for none: the table draws the person's layout.
	savedColumns: columnsFromParam(params.cols),
	// The applied saved filter's id (KPR-1642), or undefined.
	savedFilterId: params.saved,
});

/** The items page's view as any surface reads it off the address. */
export const readItemsView = searchParams => itemsViewOf(
	readUrlParams(searchParams, ITEMS_VIEW_PARAMS),
	key => searchParams.has(key)
);
